import React, { createContext, useContext, useState, useEffect } from 'react';

const AssessmentContext = createContext(null);

const generateAssessmentId = () => {
  const timestamp = Date.now().toString(36).toUpperCase();
  const random = Math.random().toString(36).substring(2, 6).toUpperCase();
  return `SBA-${timestamp}-${random}`;
};

export const AssessmentProvider = ({ children }) => {
  const [assessmentState, setAssessmentState] = useState({
    // User Info
    assessmentId: null,
    educatorId: null,
    fullName: '',
    subject: '',
    gradeLevel: '',
    domain: '',
    
    // Current Stage
    currentStage: null, // null, '1A', '1B', '2', '3', 'complete'
    
    // Stage 1A - Knowledge Test
    stage1A: {
      answers: {}, // { questionId: answer }
      markedForReview: [],
      timeRemaining: 45 * 60, // 45 minutes in seconds
      completed: false,
      score: null,
      percentage: null
    },
    
    // Stage 1B - PSDM
    stage1B: {
      answers: {}, // { scenarioId: optionIndex }
      currentScenario: 0,
      timeRemaining: 25 * 60, // 25 minutes in seconds
      completed: false,
      dimensionScores: null,
      totalScore: null,
      percentage: null
    },
    
    // Stage 2 & 3 (Video stages - placeholder for now)
    stage2: {
      completed: false,
      assessorScore: null
    },
    stage3: {
      completed: false,
      assessorScore: null
    },
    
    // Overall
    gateCheckPassed: null,
    finalScore: null,
    certificationLevel: null,
    startedAt: null,
    completedAt: null
  });

  // Load from localStorage on mount
  useEffect(() => {
    const saved = localStorage.getItem('sba_assessment_state');
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        setAssessmentState(parsed);
      } catch (e) {
        console.error('Failed to parse saved assessment state');
      }
    }
  }, []);

  // Save to localStorage on state change
  useEffect(() => {
    if (assessmentState.assessmentId) {
      localStorage.setItem('sba_assessment_state', JSON.stringify(assessmentState));
    }
  }, [assessmentState]);

  // Start new assessment
  const startAssessment = (userData) => {
    const newState = {
      ...assessmentState,
      assessmentId: generateAssessmentId(),
      educatorId: userData.educatorId,
      fullName: userData.fullName,
      subject: userData.subject,
      gradeLevel: userData.gradeLevel,
      domain: userData.domain,
      currentStage: '1A',
      startedAt: new Date().toISOString(),
      stage1A: {
        ...assessmentState.stage1A,
        timeRemaining: 45 * 60
      }
    };
    setAssessmentState(newState);
    return newState.assessmentId;
  };

  // Update Stage 1A answer
  const updateStage1AAnswer = (questionId, answer) => {
    setAssessmentState(prev => ({
      ...prev,
      stage1A: {
        ...prev.stage1A,
        answers: {
          ...prev.stage1A.answers,
          [questionId]: answer
        }
      }
    }));
  };

  // Toggle mark for review
  const toggleMarkForReview = (questionId) => {
    setAssessmentState(prev => {
      const marked = prev.stage1A.markedForReview;
      const isMarked = marked.includes(questionId);
      return {
        ...prev,
        stage1A: {
          ...prev.stage1A,
          markedForReview: isMarked
            ? marked.filter(id => id !== questionId)
            : [...marked, questionId]
        }
      };
    });
  };

  // Update Stage 1A time
  const updateStage1ATime = (timeRemaining) => {
    setAssessmentState(prev => ({
      ...prev,
      stage1A: {
        ...prev.stage1A,
        timeRemaining
      }
    }));
  };

  // Complete Stage 1A
  const completeStage1A = (score, percentage) => {
    const passed = percentage >= 50;
    setAssessmentState(prev => ({
      ...prev,
      stage1A: {
        ...prev.stage1A,
        completed: true,
        score,
        percentage
      },
      gateCheckPassed: passed,
      currentStage: passed ? '1B' : 'failed'
    }));
    return passed;
  };

  // Update Stage 1B answer
  const updateStage1BAnswer = (scenarioId, optionIndex) => {
    setAssessmentState(prev => ({
      ...prev,
      stage1B: {
        ...prev.stage1B,
        answers: {
          ...prev.stage1B.answers,
          [scenarioId]: optionIndex
        },
        currentScenario: prev.stage1B.currentScenario + 1
      }
    }));
  };

  // Update Stage 1B time
  const updateStage1BTime = (timeRemaining) => {
    setAssessmentState(prev => ({
      ...prev,
      stage1B: {
        ...prev.stage1B,
        timeRemaining
      }
    }));
  };

  // Complete Stage 1B
  const completeStage1B = (dimensionScores, totalScore, percentage) => {
    setAssessmentState(prev => ({
      ...prev,
      stage1B: {
        ...prev.stage1B,
        completed: true,
        dimensionScores,
        totalScore,
        percentage
      },
      currentStage: 'complete', // For now, skip stages 2 & 3
      completedAt: new Date().toISOString()
    }));
  };

  // Calculate and set final results
  const calculateFinalResults = (stage1APercent, stage1BPercent) => {
    // For MCQ-only version, weight 1A at 60% and 1B at 40%
    const finalScore = (stage1APercent * 0.60) + (stage1BPercent * 0.40);
    
    let certificationLevel;
    if (finalScore >= 90) certificationLevel = 'GOLD';
    else if (finalScore >= 75) certificationLevel = 'SILVER';
    else if (finalScore >= 60) certificationLevel = 'BRONZE';
    else certificationLevel = 'NOT_CERTIFIED';

    setAssessmentState(prev => ({
      ...prev,
      finalScore,
      certificationLevel
    }));

    return { finalScore, certificationLevel };
  };

  // Reset assessment
  const resetAssessment = () => {
    localStorage.removeItem('sba_assessment_state');
    setAssessmentState({
      assessmentId: null,
      educatorId: null,
      fullName: '',
      subject: '',
      gradeLevel: '',
      domain: '',
      currentStage: null,
      stage1A: {
        answers: {},
        markedForReview: [],
        timeRemaining: 45 * 60,
        completed: false,
        score: null,
        percentage: null
      },
      stage1B: {
        answers: {},
        currentScenario: 0,
        timeRemaining: 25 * 60,
        completed: false,
        dimensionScores: null,
        totalScore: null,
        percentage: null
      },
      stage2: { completed: false, assessorScore: null },
      stage3: { completed: false, assessorScore: null },
      gateCheckPassed: null,
      finalScore: null,
      certificationLevel: null,
      startedAt: null,
      completedAt: null
    });
  };

  return (
    <AssessmentContext.Provider value={{
      assessmentState,
      startAssessment,
      updateStage1AAnswer,
      toggleMarkForReview,
      updateStage1ATime,
      completeStage1A,
      updateStage1BAnswer,
      updateStage1BTime,
      completeStage1B,
      calculateFinalResults,
      resetAssessment
    }}>
      {children}
    </AssessmentContext.Provider>
  );
};

export const useAssessment = () => {
  const context = useContext(AssessmentContext);
  if (!context) {
    throw new Error('useAssessment must be used within an AssessmentProvider');
  }
  return context;
};
